import { useState, useEffect, useRef, useCallback } from 'preact/hooks';

interface UseVirtualScrollOptions {
    totalItems: number;
    itemHeight: number;
    overscan?: number;
}

interface UseVirtualScrollResult {
    containerRef: { current: HTMLDivElement | null };
    startIndex: number;
    endIndex: number;
    totalHeight: number;
    offsetY: number;
}

export function useVirtualScroll({ totalItems, itemHeight, overscan = 10 }: UseVirtualScrollOptions): UseVirtualScrollResult {
    const containerRef = useRef<HTMLDivElement>(null);
    const [range, setRange] = useState({ start: 0, end: 50 });
    const rafRef = useRef<number>(0);
    const containerHeightRef = useRef(600);

    const recalc = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const scrollTop = el.scrollTop;
        const h = el.clientHeight;
        containerHeightRef.current = h;

        const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
        const visible = Math.ceil(h / itemHeight) + 2 * overscan;
        const end = Math.min(totalItems, start + visible);

        setRange(prev => {
            if (prev.start === start && prev.end === end) return prev;
            return { start, end };
        });
    }, [totalItems, itemHeight, overscan]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(recalc);
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        recalc();

        const observer = new ResizeObserver(recalc);
        observer.observe(el);

        return () => {
            el.removeEventListener('scroll', handleScroll);
            observer.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [recalc]);

    // Recalc when totalItems changes
    useEffect(() => { recalc(); }, [totalItems, recalc]);

    const totalHeight = totalItems * itemHeight;
    const offsetY = range.start * itemHeight;

    return { containerRef, startIndex: range.start, endIndex: range.end, totalHeight, offsetY };
}
